import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useCartCtx, useAuthCtx } from '../App';
import { useCurrency } from '../hooks/useCurrency';
import api from '../utils/api';
import { productImage, onImageError } from '../utils/helpers';
import FdaDisclaimer from '../components/FdaDisclaimer';
import { trackActivity } from '../utils/track';
import { toast } from 'react-toastify';

/* ── tiny SVG helpers ───────────────────────────────────────────────────── */
function IconTruck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 inline-block mr-1.5 text-tea-600">
      <path d="M1 3h15v13H1z" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}
function IconShield() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-tea-600">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
function IconBox() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-tea-600">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}
function IconMap() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-tea-600">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  );
}
function IconVial() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-tea-600">
      <path d="M14.5 2H9.5l-7 14v3a2 2 0 0 0 2 2h15a2 2 0 0 0 2-2v-3L14.5 2z" />
    </svg>
  );
}
function IconSearch() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-tea-600">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

/* ── Bundle tier config ─────────────────────────────────────────────────── */
const BUNDLE_TIERS = [
  { qty: 1, label: 'Single Vial', badge: null,         discount: 0 },
  { qty: 2, label: '2-Vial Pack', badge: 'MOST POPULAR', badgeSub: '5% OFF',  discount: 0.05 },
  { qty: 3, label: '3+ Vial Bundle', badge: 'BEST VALUE', badgeSub: '7.5% OFF', discount: 0.075 },
];

/* ── Trust badge data ───────────────────────────────────────────────────── */
const TRUST_BADGES = [
  { icon: <IconSearch />, title: 'Third-Party Tested', sub: 'CoA on every lot' },
  { icon: <IconShield />, title: '≥99% Purity', sub: 'HPLC verified' },
  { icon: <IconMap />,    title: 'USA Based', sub: 'Domestic fulfillment' },
  { icon: <IconTruck />,  title: 'Discreet Shipping', sub: 'Tracked' },
  { icon: <IconBox />,    title: 'Batch Traceable', sub: 'Lot # on every vial' },
  { icon: <IconVial />,   title: 'Lyophilized Powder', sub: 'Long shelf life' },
];

/* ── Primary mechanism defaults ─────────────────────────────────────────── */
const DEFAULT_MECHANISMS = [
  '99%+ HPLC purity on every batch',
  'Third-party tested with CoA per lot',
  'Ships fast & discreet — tracked nationwide',
];

export default function ProductPage() {
  const { slug } = useParams();
  const { fmt } = useCurrency();

  const [product, setProduct]                 = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity]               = useState(1);
  const [activeTier, setActiveTier]           = useState(0); // index into BUNDLE_TIERS
  const [loading, setLoading]                 = useState(true);

  const { addItem } = useCartCtx();
  const { isMember } = useAuthCtx();

  useEffect(() => {
    api.get('/products/' + slug)
      .then(res => {
        setProduct(res.data);
        const variants = res.data.variants || [];
        const firstInStock = variants.find(v => v.inventory >= 1);
        setSelectedVariant(firstInStock || variants[0]);
        // Behavioral signal: record that this product was viewed (non-blocking).
        trackActivity({
          type: 'product_view',
          productId: res.data.id,
          productSlug: res.data.slug || slug,
          productName: res.data.name,
        });
      })
      .catch(() => toast.error('Product not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  /* Sync quantity when tile is selected */
  const selectTier = (idx) => {
    setActiveTier(idx);
    setQuantity(BUNDLE_TIERS[idx].qty);
  };

  /* ── membership-aware pricing for the selected variant ──────────────── */
  const vipPrice    = selectedVariant?.priceInCents ?? 0;
  const retailPrice = (selectedVariant?.comparePriceInCents && selectedVariant.comparePriceInCents > vipPrice)
    ? selectedVariant.comparePriceInCents
    : vipPrice;
  const hasVipDeal  = retailPrice > vipPrice;
  // The shopper pays the VIP price if they're a member, else retail.
  const tierDiscount = BUNDLE_TIERS[activeTier]?.discount ?? 0;
  const basePrice    = isMember ? vipPrice : retailPrice;
  const tierPrice    = Math.round(basePrice * (1 - tierDiscount));

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center text-tea-600 text-lg">Loading…</div>
  );
  if (!product) return (
    <div className="min-h-screen flex items-center justify-center text-gray-500">Product not found.</div>
  );

  const mechanisms = product.mechanisms?.length
    ? product.mechanisms
    : DEFAULT_MECHANISMS;

  const isOutOfStock = selectedVariant && selectedVariant.inventory < 1;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-tea-50 to-white">
      <Navbar />

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12">

        {/* ── Two-column grid ───────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start">

          {/* ── LEFT: Hero vial panel ──────────────────────────────────── */}
          <div className="sticky top-24">
            <div className="relative rounded-3xl shadow-lg overflow-hidden min-h-[480px]">
              <img
                src={productImage(product.slug)}
                onError={onImageError}
                alt={product.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>

          {/* ── RIGHT: Product details ─────────────────────────────────── */}
          <div className="flex flex-col gap-6">

            {/* 1. Product title */}
            <div>
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 leading-tight mb-2">
                {product.name}
              </h1>
            </div>

            {/* 2. Spec cards: VOLUME + PURITY */}
            <div className="grid grid-cols-2 gap-3">
              {/* Volume */}
              <div className="bg-white rounded-2xl shadow-md p-4 flex flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <IconVial />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Volume</span>
                </div>
                <span className="text-2xl font-black text-gray-900">
                  {selectedVariant?.name ?? product.variants?.[0]?.name ?? '—'}
                </span>
              </div>
              {/* Purity */}
              <div className="bg-white rounded-2xl shadow-md p-4 flex flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <IconShield />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Purity</span>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-black text-gray-900">99%+</span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-tea-600">HPLC</span>
                </div>
              </div>
            </div>

            {/* 3. Variant selector */}
            {product.variants && product.variants.length > 1 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Choose size</p>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map(v => (
                    <button
                      key={v.id}
                      onClick={() => setSelectedVariant(v)}
                      className={
                        'px-4 py-2 rounded-full border-2 text-sm font-semibold transition-all ' +
                        (selectedVariant?.id === v.id
                          ? 'border-tea-600 bg-tea-50 text-tea-700 shadow-sm'
                          : 'border-gray-200 text-gray-600 hover:border-tea-300')
                      }
                    >
                      {v.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 4. Price block */}
            {selectedVariant && (
              <div className="bg-white rounded-2xl shadow-md p-5">
                <div className="flex items-baseline gap-3 mb-1">
                  <span className="text-4xl font-black text-gray-900">{fmt(tierPrice)}</span>
                  {isMember && hasVipDeal && (
                    <span className="text-xl text-gray-400 line-through">{fmt(retailPrice)}</span>
                  )}
                  {isMember && (
                    <span className="text-[11px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 rounded-full px-2 py-0.5">★ VIP</span>
                  )}
                  {retailPrice > tierPrice && (
                    <span className="ml-auto text-xs font-bold uppercase tracking-wide bg-tea-50 text-tea-700 rounded-full px-2.5 py-1">
                      {Math.round((1 - tierPrice / retailPrice) * 100)}% OFF
                    </span>
                  )}
                </div>

                {/* Non-member: surface the VIP price they could pay instead. */}
                {!isMember && hasVipDeal && (
                  <Link
                    to="/membership"
                    className="mt-1 mb-2 flex items-center justify-between gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 hover:border-amber-300 transition-colors"
                  >
                    <span className="text-sm text-amber-800">
                      <span className="font-bold">★ VIP price {fmt(Math.round(vipPrice * (1 - tierDiscount)))}</span>
                      <span className="text-amber-600"> · save {fmt(Math.round((retailPrice - vipPrice) * (1 - tierDiscount)))}</span>
                    </span>
                    <span className="text-xs font-bold text-amber-700 whitespace-nowrap">Unlock →</span>
                  </Link>
                )}

                <p className="text-xs font-bold uppercase tracking-widest text-tea-600 mb-1">Bundle &amp; Save</p>
                <p className="text-xs text-gray-400 leading-relaxed">
                  Volume pricing: the more vials you order, the lower the per-vial cost.
                  Ships as lyophilized powder; requires bacteriostatic water to reconstitute.
                </p>
              </div>
            )}

            {/* 5. Bundle tier selector */}
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Select quantity</p>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {BUNDLE_TIERS.map((tier, idx) => (
                  <button
                    key={tier.qty}
                    onClick={() => selectTier(idx)}
                    className={
                      'relative flex flex-col items-center justify-center rounded-2xl border-2 px-2 py-3 text-center transition-all ' +
                      (activeTier === idx
                        ? 'border-tea-600 bg-tea-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-tea-300')
                    }
                  >
                    {/* badge */}
                    {tier.badge && (
                      <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-tea-600 text-white text-[9px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 whitespace-nowrap">
                        {tier.badge}
                      </span>
                    )}
                    {/* blue dollar sign */}
                    <span className="flex items-center justify-center mb-1.5 h-6">
                      <span className="text-2xl font-extrabold text-blue-600 leading-none">$</span>
                    </span>
                    <span className="text-xs font-bold text-gray-800 leading-tight">{tier.label}</span>
                    {tier.badgeSub && (
                      <span className="text-[10px] font-semibold text-tea-600 mt-0.5">{tier.badgeSub}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Quantity stepper pill */}
              <div className="flex items-center justify-center">
                <div className="flex items-center bg-white border border-gray-200 rounded-full shadow-sm overflow-hidden">
                  <button
                    onClick={() => {
                      const next = Math.max(1, quantity - 1);
                      setQuantity(next);
                      // find closest tier
                      const tIdx = BUNDLE_TIERS.reduce((best, t, i) => t.qty <= next ? i : best, 0);
                      setActiveTier(tIdx);
                    }}
                    className="w-10 h-10 flex items-center justify-center text-xl text-gray-500 hover:text-tea-700 transition-colors"
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>
                  <span className="w-10 text-center font-bold text-gray-900 text-sm">{quantity}</span>
                  <button
                    onClick={() => {
                      const next = quantity + 1;
                      setQuantity(next);
                      // upgrade tier if applicable
                      const tIdx = BUNDLE_TIERS.reduce((best, t, i) => t.qty <= next ? i : best, 0);
                      setActiveTier(tIdx);
                    }}
                    className="w-10 h-10 flex items-center justify-center text-xl text-gray-500 hover:text-tea-700 transition-colors"
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* 6. Shipping line — VIP perk vs threshold */}
            <p className="text-sm text-tea-600 font-semibold flex items-center gap-1">
              <IconTruck />
              {isMember ? 'Free VIP shipping on every order' : 'Free shipping over $200 — or free with VIP'}
            </p>

            {/* 7. Add to Cart */}
            <button
              onClick={() => {
                addItem(selectedVariant, product, quantity);
                toast.success('Added to cart!');
              }}
              disabled={isOutOfStock || !selectedVariant}
              className={
                'w-full py-4 rounded-full text-white text-lg font-bold tracking-wide transition-all shadow-lg ' +
                (isOutOfStock
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-tea-600 hover:bg-tea-700 active:scale-[0.98]')
              }
            >
              {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
            </button>

            {/* 8. Trust badge grid */}
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Why Aloha Amino</p>
              <div className="grid grid-cols-2 gap-2">
                {TRUST_BADGES.map((badge, i) => (
                  <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 flex items-start gap-2.5">
                    <div className="mt-0.5 shrink-0">{badge.icon}</div>
                    <div>
                      <p className="text-xs font-bold text-gray-800 leading-tight">{badge.title}</p>
                      <p className="text-[11px] text-gray-400 leading-snug">{badge.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 9. Description */}
            {product.description && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Description</p>
                <p className="text-base text-gray-700 leading-relaxed">{product.description}</p>
              </div>
            )}

            {/* 10. Primary mechanism rows */}
            <div className="flex flex-col gap-4">
              {mechanisms.slice(0, 3).map((mech, i) => (
                <div key={i}>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-0.5">
                    Primary Mechanism {String(i + 1).padStart(2, '0')}/{String(mechanisms.slice(0, 3).length).padStart(2, '0')}
                  </p>
                  <p className="text-sm font-bold text-gray-800">{mech}</p>
                </div>
              ))}
            </div>

            {/* 11. Compliance disclaimer */}
            <FdaDisclaimer />
            <p className="text-xs text-gray-400 bg-gray-50 border border-gray-200 rounded-xl p-3 leading-relaxed">
              <strong className="text-gray-500">For laboratory research use only.</strong>{' '}
              Not for human or veterinary consumption. By purchasing you confirm you are a
              licensed researcher or qualified lab provider, and agree not to administer or
              represent this compound for therapeutic use.
            </p>

          </div>{/* end right column */}
        </div>{/* end grid */}
      </main>

      <Footer />
    </div>
  );
}
